import React from 'react';
import { Select, MenuItem, Chip, Box } from '@mui/material';
import { useSession } from '../permissions/SessionContext';
import { colors, radius } from '../design-system/tokens';

const LIVE_OPTION = '__live__';

/**
 * Mock 세션 전환기 — 개발/테스트 전용.
 *
 * production 빌드에서는 NODE_ENV 가 상수로 치환되어 아래 early return 이 항상 실행되고,
 * 나머지 본문(옵션 문자열 포함)은 dead code 로 번들에서 제거된다.
 * OwnerConsoleLayout 쪽에서도 동일 조건으로 렌더링 자체를 막는다 (이중 방어).
 */
const SessionSwitcher = () => {
  // 훅 규칙: useSession 은 조건 없이 항상 호출 (early return 은 그 뒤에)
  const { mode, mockKey, mockSessions, switchToMock, switchToLive } = useSession();

  if (process.env.NODE_ENV === 'production') return null;

  const value = mode === 'live' ? LIVE_OPTION : mockKey;

  return (
    <Box
      sx={{
        display: 'inline-grid',
        gridTemplateColumns: 'auto minmax(0, 1fr)',
        alignItems: 'center',
        gap: 0.75,
        width: '100%',
        minHeight: 40,
        px: 0.75,
        py: 0.5,
        bgcolor: colors.surface,
        border: `1px solid ${colors.border.muted}`,
        borderRadius: `${radius.button}px`,
        boxSizing: 'border-box',
      }}
    >
      <Chip
        label={mode === 'live' ? 'LIVE' : 'MOCK'}
        size="small"
        sx={{
          bgcolor: mode === 'live' ? colors.status.success.bg : colors.status.warning.bg,
          color: mode === 'live' ? colors.status.success.fg : colors.status.warning.fg,
          border: `1px solid ${
            mode === 'live' ? colors.status.success.border : colors.status.warning.border
          }`,
          borderRadius: `${radius.chip}px`,
          height: 24,
          fontSize: 11,
          fontWeight: 800,
          '& .MuiChip-label': {
            px: 0.9,
          },
        }}
      />
      <Select
        size="small"
        value={value}
        onChange={(e) =>
          e.target.value === LIVE_OPTION ? switchToLive() : switchToMock(e.target.value)
        }
        aria-label="체험 세션 전환"
        sx={{
          minWidth: 0,
          bgcolor: 'transparent',
          color: colors.text.primary,
          fontSize: 13.5,
          fontWeight: 800,
          height: 30,
          '& .MuiSelect-select': {
            minHeight: '0 !important',
            py: 0.25,
            pl: 0.5,
            pr: '26px !important',
            display: 'flex',
            alignItems: 'center',
          },
          '& .MuiOutlinedInput-notchedOutline': {
            border: 0,
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            border: 0,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            border: 0,
            boxShadow: 'none',
          },
          '& .MuiSelect-icon': {
            right: 2,
            color: colors.text.muted,
          },
        }}
      >
        <MenuItem value={LIVE_OPTION} sx={{ fontSize: 14 }}>
          실제 세션
        </MenuItem>
        {Object.values(mockSessions).map((s) => (
          <MenuItem key={s.key} value={s.key} sx={{ fontSize: 14 }}>
            {s.label} (mock)
          </MenuItem>
        ))}
      </Select>
    </Box>
  );
};

export default SessionSwitcher;
